// src/utils/bible/indexedDB.js
// IndexedDB wrapper for Bible data storage.
// Not meant to be imported directly by page components — use bibleService.js.
const DB_NAME = "BibleCacheDB";
const DB_VERSION = 1;
const STORE_NAME = "bibleData";
const INDEX_STORE = "bibleIndex";

let dbInstance = null;

// ─── DB initialisation ────────────────────────────────────────────────────────

export async function initBibleDB() {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    let request;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      console.error("[IndexedDB] Failed to open database:", err);
      reject(err);
      return;
    }

    request.onerror = () => {
      console.error("[IndexedDB] Failed to open database:", request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      dbInstance.onerror = (event) => {
        // Catch unhandled transaction errors so they don't silently swallow problems
        console.error("[IndexedDB] Unhandled DB error:", event.target.error);
      };
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const bibleStore = db.createObjectStore(STORE_NAME, {
          keyPath: "versionId"
        });
        bibleStore.createIndex("versionId", "versionId", { unique: true });
        bibleStore.createIndex("cachedAt", "cachedAt", { unique: false });
      }

      if (!db.objectStoreNames.contains(INDEX_STORE)) {
        const indexStore = db.createObjectStore(INDEX_STORE, {
          keyPath: "id",
          autoIncrement: true
        });
        indexStore.createIndex("versionId", "versionId", { unique: false });
        indexStore.createIndex("book", "book", { unique: false });
        indexStore.createIndex("chapter", "chapter", { unique: false });
        indexStore.createIndex("reference", ["versionId", "book", "chapter", "verse"], { unique: true });
      }
    };
  });
}

// ─── Store ────────────────────────────────────────────────────────────────────

/**
 * Stores the main Bible JSON blob quickly — no verse-level indexing.
 * Use this on the critical path; call indexBibleVerses separately in the
 * background.
 */
export async function storeBibleDataMain(versionId, bibleData) {
  if (!versionId || !bibleData) {
    console.warn("[IndexedDB] storeBibleDataMain: missing versionId or data");
    return false;
  }

  try {
    const db = await initBibleDB();
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    await new Promise((resolve, reject) => {
      const request = store.put({
        versionId,
        data: bibleData,
        cachedAt: Date.now(),
        verseCount: bibleData?.verses?.length || 0
      });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    return true;
  } catch (error) {
    console.error("[IndexedDB] storeBibleDataMain failed:", error);
    throw error;
  }
}

/**
 * Stores Bible data and kicks off background verse indexing.
 */
export async function storeBibleData(versionId, bibleData) {
  await storeBibleDataMain(versionId, bibleData);

  if (Array.isArray(bibleData?.verses)) {
    indexBibleVerses(versionId, bibleData.verses).catch((err) => {
      console.warn("[IndexedDB] Background verse indexing failed:", err);
    });
  }

  return true;
}

// ─── Verse indexing ───────────────────────────────────────────────────────────

/**
 * Indexes all verses for a version into the INDEX_STORE in batches.
 * Always awaits initBibleDB() properly — never accesses dbInstance directly.
 */
export async function indexBibleVerses(versionId, verses) {
  if (!versionId || !Array.isArray(verses) || verses.length === 0) {
    console.warn("[IndexedDB] indexBibleVerses: nothing to index");
    return;
  }

  let db;
  try {
    db = await initBibleDB();
  } catch (err) {
    console.error("[IndexedDB] indexBibleVerses: DB init failed:", err);
    throw err;
  }

  return new Promise((resolve, reject) => {
    indexBibleVersesInternal(versionId, verses, db, resolve, reject);
  });
}

function indexBibleVersesInternal(versionId, verses, db, resolve, reject) {
  const BATCH_SIZE = 1000;
  let currentIndex = 0;
  let totalIndexed = 0;

  // Clear any existing index entries for this version first
  try {
    const clearTx = db.transaction([INDEX_STORE], "readwrite");
    const clearStore = clearTx.objectStore(INDEX_STORE);
    const clearIndex = clearStore.index("versionId");
    const clearRequest = clearIndex.openKeyCursor(IDBKeyRange.only(versionId));

    clearRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        clearStore.delete(cursor.primaryKey);
        cursor.continue();
      } else {
        indexBatch();
      }
    };

    clearRequest.onerror = () => {
      console.warn("[IndexedDB] Error clearing old index entries, continuing anyway:", clearRequest.error);
      indexBatch();
    };
  } catch (err) {
    console.warn("[IndexedDB] Could not clear index, continuing:", err);
    indexBatch();
  }

  function indexBatch() {
    if (currentIndex >= verses.length) {
      console.log(`[IndexedDB] Indexed ${totalIndexed} verses for version: ${versionId}`);
      resolve();
      return;
    }

    const batch = verses.slice(currentIndex, currentIndex + BATCH_SIZE);

    let tx;
    try {
      tx = db.transaction([INDEX_STORE], "readwrite");
    } catch (err) {
      console.error("[IndexedDB] Failed to open transaction for batch:", err);
      reject(err);
      return;
    }

    const store = tx.objectStore(INDEX_STORE);
    let completed = 0;

    tx.onerror = () => {
      console.error("[IndexedDB] Transaction error during indexing:", tx.error);
      reject(tx.error);
    };

    batch.forEach((verse, idx) => {
      if (!verse) {
        completed++;
        if (completed === batch.length) advanceBatch();
        return;
      }

      const entry = {
        versionId,
        book: verse.book_name || verse.book,
        chapter: String(verse.chapter),
        verse: String(verse.verse),
        text: verse.text,
        index: currentIndex + idx
      };

      const req = store.add(entry);

      req.onsuccess = () => {
        completed++;
        if (completed === batch.length) advanceBatch();
      };

      req.onerror = () => {
        // Non-fatal: duplicate key or constraint violation — log and continue
        console.warn(`[IndexedDB] Could not index verse at position ${currentIndex + idx}:`, req.error?.message);
        completed++;
        if (completed === batch.length) advanceBatch();
      };
    });

    function advanceBatch() {
      totalIndexed += batch.length;
      currentIndex += BATCH_SIZE;

      if (totalIndexed % 5000 === 0 || currentIndex >= verses.length) {
        console.log(`[IndexedDB] Indexed ${totalIndexed}/${verses.length} verses (${Math.round((totalIndexed / verses.length) * 100)}%)`);
      }

      // Yield to the event loop between batches to avoid blocking the UI
      setTimeout(() => indexBatch(), 0);
    }
  }
}

// ─── Retrieve ─────────────────────────────────────────────────────────────────

export async function getBibleDataFromDB(versionId) {
  if (!versionId) return null;

  try {
    const db = await initBibleDB();
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.get(versionId);
      request.onsuccess = () => resolve(request.result?.data || null);
      request.onerror = () => {
        console.error("[IndexedDB] getBibleDataFromDB failed:", request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error("[IndexedDB] getBibleDataFromDB failed:", error);
    return null;
  }
}

export async function isBibleDataCached(versionId, ttl = 24 * 60 * 60 * 1000) {
  if (!versionId) return false;

  try {
    const db = await initBibleDB();
    const transaction = db.transaction([STORE_NAME], "readonly");
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.get(versionId);
      request.onsuccess = () => {
        if (!request.result) {
          resolve(false);
          return;
        }
        const age = Date.now() - request.result.cachedAt;
        resolve(age < ttl);
      };
      request.onerror = () => {
        console.error("[IndexedDB] isBibleDataCached failed:", request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error("[IndexedDB] isBibleDataCached failed:", error);
    return false;
  }
}

// ─── Search ───────────────────────────────────────────────────────────────────

export async function searchVersesInDB(versionId, searchTerm) {
  if (!versionId || !searchTerm?.trim()) return [];

  try {
    const db = await initBibleDB();
    const transaction = db.transaction([INDEX_STORE], "readonly");
    const store = transaction.objectStore(INDEX_STORE);
    const index = store.index("versionId");
    const searchLower = searchTerm.toLowerCase().trim();

    return new Promise((resolve, reject) => {
      const results = [];
      const request = index.openCursor(IDBKeyRange.only(versionId));

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const v = cursor.value;
          const searchText = `${v.book || ""} ${v.chapter || ""}:${v.verse || ""} ${v.text || ""}`.toLowerCase();
          if (searchText.includes(searchLower)) {
            results.push({
              book: v.book,
              chapter: v.chapter,
              verse: v.verse,
              text: v.text,
              fullReference: `${v.book} ${v.chapter}:${v.verse}`
            });
          }
          cursor.continue();
        } else {
          resolve(results.slice(0, 100));
        }
      };

      request.onerror = () => {
        console.error("[IndexedDB] searchVersesInDB failed:", request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error("[IndexedDB] searchVersesInDB failed:", error);
    return [];
  }
}

// ─── Book / chapter / verse lookups ──────────────────────────────────────────

export async function getBooksFromDB(versionId) {
  if (!versionId) return [];

  try {
    const db = await initBibleDB();
    const transaction = db.transaction([INDEX_STORE], "readonly");
    const store = transaction.objectStore(INDEX_STORE);
    const index = store.index("versionId");

    return new Promise((resolve, reject) => {
      const books = new Set();
      const request = index.openCursor(IDBKeyRange.only(versionId));

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.book) books.add(cursor.value.book);
          cursor.continue();
        } else {
          resolve(Array.from(books).filter(Boolean).sort());
        }
      };

      request.onerror = () => {
        console.error("[IndexedDB] getBooksFromDB failed:", request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error("[IndexedDB] getBooksFromDB failed:", error);
    return [];
  }
}

export async function getChaptersFromDB(versionId, book) {
  if (!versionId || !book) return [];

  try {
    const db = await initBibleDB();
    const transaction = db.transaction([INDEX_STORE], "readonly");
    const store = transaction.objectStore(INDEX_STORE);
    const index = store.index("versionId");

    return new Promise((resolve, reject) => {
      const chapters = new Set();
      const request = index.openCursor(IDBKeyRange.only(versionId));

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.book === book && cursor.value.chapter) {
            chapters.add(cursor.value.chapter);
          }
          cursor.continue();
        } else {
          resolve(
            Array.from(chapters)
              .filter(Boolean)
              .sort((a, b) => Number(a) - Number(b))
          );
        }
      };

      request.onerror = () => {
        console.error("[IndexedDB] getChaptersFromDB failed:", request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error("[IndexedDB] getChaptersFromDB failed:", error);
    return [];
  }
}

export async function getVersesFromDB(versionId, book, chapter) {
  if (!versionId || !book || !chapter) return [];

  try {
    const db = await initBibleDB();
    const transaction = db.transaction([INDEX_STORE], "readonly");
    const store = transaction.objectStore(INDEX_STORE);
    const index = store.index("reference");

    return new Promise((resolve, reject) => {
      const verses = new Set();
      const range = IDBKeyRange.bound([versionId, book, chapter, ""], [versionId, book, chapter, "\uffff"]);
      const request = index.openCursor(range);

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.verse) verses.add(cursor.value.verse);
          cursor.continue();
        } else {
          resolve(
            Array.from(verses)
              .filter(Boolean)
              .sort((a, b) => Number(a) - Number(b))
          );
        }
      };

      request.onerror = () => {
        console.error("[IndexedDB] getVersesFromDB failed:", request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error("[IndexedDB] getVersesFromDB failed:", error);
    return [];
  }
}

export async function getVerseFromDB(versionId, book, chapter, verse) {
  if (!versionId || !book || !chapter || !verse) return "";

  try {
    const db = await initBibleDB();
    const transaction = db.transaction([INDEX_STORE], "readonly");
    const store = transaction.objectStore(INDEX_STORE);
    const index = store.index("reference");

    return new Promise((resolve, reject) => {
      const key = [versionId, book, String(chapter), String(verse)];
      const request = index.get(key);

      request.onsuccess = () => resolve(request.result?.text || "");
      request.onerror = () => {
        console.error("[IndexedDB] getVerseFromDB failed:", request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error("[IndexedDB] getVerseFromDB failed:", error);
    return "";
  }
}

// ─── Cache management ─────────────────────────────────────────────────────────

export async function clearBibleCache(versionId) {
  if (!versionId) return false;

  try {
    const db = await initBibleDB();

    // Delete main data blob
    const dataTx = db.transaction([STORE_NAME], "readwrite");
    const dataStore = dataTx.objectStore(STORE_NAME);
    await new Promise((resolve, reject) => {
      const req = dataStore.delete(versionId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });

    // Delete all index entries for this version
    const indexTx = db.transaction([INDEX_STORE], "readwrite");
    const indexStore = indexTx.objectStore(INDEX_STORE);
    const idx = indexStore.index("versionId");
    await new Promise((resolve, reject) => {
      const req = idx.openKeyCursor(IDBKeyRange.only(versionId));
      req.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          indexStore.delete(cursor.primaryKey);
          cursor.continue();
        } else {
          resolve();
        }
      };
      req.onerror = () => reject(req.error);
    });

    return true;
  } catch (error) {
    console.error("[IndexedDB] clearBibleCache failed:", error);
    return false;
  }
}

export function isIndexedDBSupported() {
  try {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  } catch {
    return false;
  }
}
